class LocalStorageCacher {
    /**
     * Field prefix.
     *
     * @type {string}
     * @private
     */
    prefix;
    /**
     * Cacher constructor.
     *
     * @param {string} prefix
     */
    constructor(prefix) {
        this.prefix = prefix ?? "cacher-";
    }
    /**
     * Delete data by key.
     *
     * @param {string} key
     */
    del(key) {
        if (typeof window !== "undefined") {
            localStorage.removeItem(`${this.prefix}${key}`);
        }
    }
    /**
     * Get data by key.
     *
     * @param {string} key
     * @return {T | null}
     */
    get(key) {
        if (typeof window === "undefined") {
            return null;
        }
        const raw = localStorage.getItem(`${this.prefix}${key}`);
        if (raw === null) {
            return null;
        }
        const content = JSON.parse(raw);
        if (content.expiry !== null && content.expiry <= Date.now()) {
            this.del(key);
            return null;
        }
        return content.payload;
    }
    /**
     * Check the key for existence.
     *
     * @param {string} key
     * @return {boolean}
     */
    has(key) {
        if (typeof window === "undefined") {
            return false;
        }
        const raw = localStorage.getItem(`${this.prefix}${key}`);
        if (raw === null) {
            return false;
        }
        const content = JSON.parse(raw);
        if (content.expiry !== null && content.expiry <= Date.now()) {
            this.del(key);
            return false;
        }
        return true;
    }
    /**
     * Clear storage.
     */
    purge() {
        if (typeof window === "undefined") {
            return;
        }
        const length = localStorage.length;
        const items = [];
        for (let index = 0; index < length; index++) {
            if (localStorage.key(index)?.includes(this.prefix) === true) {
                items.push(localStorage.key(index));
            }
        }
        items.forEach((item) => {
            if (item !== null) {
                localStorage.removeItem(item);
            }
        });
    }
    /**
     * Put data into storage.
     *
     * @param {string} key
     * @param {T} data
     * @param {Options} options
     */
    put(key, data, options) {
        if (typeof window === "undefined") {
            return;
        }
        const expiry = typeof options?.ttl === "number"
            ? Date.now() + options.ttl
            : null;
        const content = {
            expiry,
            payload: data
        };
        const raw = JSON.stringify(content);
        localStorage.setItem(`${this.prefix}${key}`, raw);
    }
    deleteExpired() {
        if (typeof window === "undefined") {
            return;
        }
        const length = localStorage.length;
        for (let index = 0; index < length; index++) {
            const key = localStorage.key(index);
            if (key?.includes(this.prefix) === true) {
                const raw = localStorage.getItem(key);
                if (raw === null) {
                    continue;
                }
                const content = JSON.parse(raw);
                if (content.expiry !== null && content.expiry <= Date.now()) {
                    this.del(key.replace(this.prefix, ""));
                }
            }
        }
    }
}

class CacherError extends Error {
    constructor(message) {
        super(message);
        this.name = "CacherError";
    }
}

class IndexedDbCacher {
    dbName;
    storeName;
    indexName = "expiry";
    quotaLimit;
    transactionTimeout;
    purgingTimeout;
    batchSize;
    exponentialBackoffBaseMs = 50;
    dbVersion = 1;
    isPurging = false;
    purgingTimer = null;
    activeConnections = new Set();
    constructor(options) {
        this.dbName = options?.dbName ?? "app-database";
        this.storeName = options?.storeName ?? "cache";
        this.quotaLimit = options?.quotaLimitMb ?? 3;
        this.transactionTimeout = options?.transactionTimeoutMs ?? 5000;
        this.purgingTimeout = options?.purgingTimeoutMs ?? 10000;
        this.batchSize = options?.batchSize ?? 50;
    }
    async withRetry(fn, maxAttempts = 3, attempt = 1) {
        try {
            return await fn();
        }
        catch (e) {
            const message = e?.message || String(e);
            const name = e?.name || '';
            const isTransient = name === 'AbortError' ||
                name === 'TimeoutError' ||
                name === 'UnknownError' ||
                name === 'VersionError' ||
                name === 'InvalidStateError' ||
                /Transaction timed out|was aborted|The transaction was aborted|connection closed/i.test(message);
            if (isTransient && attempt < maxAttempts) {
                const delay = this.exponentialBackoffBaseMs * Math.pow(2, attempt - 1);
                await new Promise(r => setTimeout(r, delay));
                return this.withRetry(fn, maxAttempts, attempt + 1);
            }
            throw e;
        }
    }
    setPurging(value) {
        this.isPurging = value;
        if (this.purgingTimer) {
            clearTimeout(this.purgingTimer);
            this.purgingTimer = null;
        }
        if (value) {
            // Safety timeout - auto-reset isPurging after 10s to prevent deadlock
            this.purgingTimer = setTimeout(() => {
                console.warn('IndexedDB: Purging flag forcefully reset after timeout');
                this.isPurging = false;
                this.purgingTimer = null;
            }, this.purgingTimeout);
        }
    }
    async waitForNotPurging() {
        if (!this.isPurging)
            return;
        const waitTimeout = Math.max(this.purgingTimeout - 500, 100);
        const start = Date.now();
        await new Promise((resolve, reject) => {
            const check = () => {
                if (!this.isPurging)
                    return resolve();
                if (Date.now() - start >= waitTimeout) {
                    return reject(new CacherError('Timed out waiting for purge to finish'));
                }
                setTimeout(check, 50);
            };
            check();
        });
    }
    closeAllConnections() {
        for (const db of this.activeConnections) {
            try {
                db.close();
            }
            catch (_) { /* ignore */ }
        }
        this.activeConnections.clear();
    }
    trackConnection(db) {
        this.activeConnections.add(db);
    }
    untrackConnection(db) {
        this.activeConnections.delete(db);
    }
    isQuotaExceededError(e) {
        const name = e?.name || e?.target?.error?.name || '';
        const message = e?.message || e?.target?.error?.message || '';
        return /QuotaExceededError/i.test(name) || /QuotaExceededError/i.test(message) || /quota.?exceeded/i.test(message);
    }
    async del(key) {
        await this.waitForNotPurging();
        return this.withRetry(() => this.executeTransaction('readwrite', (store) => store.delete(key)));
    }
    async get(key) {
        await this.waitForNotPurging();
        return this.withRetry(async () => {
            const result = await this.executeTransaction('readonly', (store) => store.get(key));
            if (result) {
                const { payload, expiry } = result;
                // Check if expired
                if (expiry !== null && expiry < Date.now()) {
                    // Lazy delete expired item (fire and forget)
                    this.del(key).catch(() => { });
                    return null;
                }
                return JSON.parse(payload);
            }
            return null;
        });
    }
    async has(key) {
        await this.waitForNotPurging();
        return this.withRetry(async () => {
            const result = await this.executeTransaction('readonly', (store) => store.get(key));
            if (result === undefined) {
                return false;
            }
            const { expiry } = result;
            return expiry === null || expiry >= Date.now();
        });
    }
    async purge() {
        await this.waitForNotPurging();
        this.setPurging(true);
        try {
            return await this.withRetry(() => this.executeTransaction('readwrite', (store) => store.clear()));
        }
        finally {
            this.setPurging(false);
        }
    }
    async put(key, data, options) {
        await this.waitForNotPurging();
        const attemptPut = () => {
            const expiry = options?.ttl ? options.ttl + Date.now() : null;
            return this.executeTransaction('readwrite', (store) => store.put({ payload: JSON.stringify(data), expiry }, key));
        };
        try {
            await this.withRetry(attemptPut);
        }
        catch (e) {
            if (!this.isQuotaExceededError(e)) {
                throw e instanceof Error ? e : new CacherError(String(e));
            }
            console.warn(`IndexedDB: QuotaExceededError on put("${key}"), attempting cleanup...`);
            // 1) Try to clean up expired items
            try {
                await this.withRetry(() => this.deleteExpired());
                console.warn('IndexedDB: deleteExpired completed');
            }
            catch (_) {
                console.warn('IndexedDB: deleteExpired failed, trying other ways');
            }
            try {
                await this.withRetry(attemptPut);
                return;
            }
            catch (e2) {
                if (!this.isQuotaExceededError(e2)) {
                    throw e2 instanceof Error ? e2 : new CacherError(String(e2));
                }
                // 2) Hard measure: delete the database and try again
                console.warn('IndexedDB: Still over quota after deleteExpired, deleting entire database...');
                try {
                    this.setPurging(true);
                    await this.deleteDBase();
                    console.warn('IndexedDB: Database deleted successfully');
                }
                catch (deleteErr) {
                    console.error('IndexedDB: deleteDBase failed:', deleteErr);
                    throw deleteErr;
                }
                finally {
                    this.setPurging(false);
                }
                await this.withRetry(attemptPut);
                console.warn(`IndexedDB: put("${key}") succeeded after database reset`);
            }
        }
    }
    async deleteExpired() {
        await this.waitForNotPurging();
        this.setPurging(true);
        try {
            return await this.withRetry(() => this.deleteExpiredBatch());
        }
        finally {
            this.setPurging(false);
        }
    }
    async deleteExpiredBatch() {
        let hasMore = true;
        while (hasMore) {
            hasMore = await new Promise((resolve, reject) => {
                let settled = false;
                let tx = null;
                let db = null;
                const cleanup = () => {
                    clearTimeout(timeoutId);
                    if (db) {
                        this.untrackConnection(db);
                        try {
                            db.close();
                        }
                        catch (e) {
                            // Ignore
                        }
                    }
                };
                const safeReject = (error) => {
                    if (settled)
                        return;
                    settled = true;
                    cleanup();
                    reject(error);
                };
                const safeResolve = (value) => {
                    if (settled)
                        return;
                    settled = true;
                    resolve(value);
                };
                const timeoutId = setTimeout(() => {
                    try {
                        tx?.abort();
                    }
                    catch (_) { /* ignore */ }
                    safeReject(new CacherError('Failed to delete expired cache entries: operation timed out'));
                }, this.transactionTimeout);
                const handle = window.indexedDB.open(this.dbName, this.dbVersion);
                handle.onerror = (e) => {
                    const error = e.target?.error;
                    safeReject(new CacherError(`Failed to open cache database: ${error?.message || JSON.stringify(error)}`));
                };
                handle.onupgradeneeded = (e) => {
                    const request = e.target;
                    const database = request.result;
                    if (!database) {
                        request.transaction?.abort();
                        safeReject(new CacherError("Failed to upgrade cache database"));
                        return;
                    }
                    let store;
                    if (!database.objectStoreNames.contains(this.storeName)) {
                        store = database.createObjectStore(this.storeName);
                    }
                    else {
                        store = request.transaction.objectStore(this.storeName);
                    }
                    // Always ensure index exists
                    if (!store.indexNames.contains(this.indexName)) {
                        store.createIndex(this.indexName, this.indexName);
                    }
                };
                handle.onsuccess = (e) => {
                    try {
                        db = e.target.result;
                        this.trackConnection(db);
                        tx = db.transaction(this.storeName, "readwrite");
                        tx.oncomplete = () => {
                            cleanup();
                        };
                        tx.onabort = (e) => {
                            e.stopPropagation?.();
                            cleanup();
                            if (!settled) {
                                settled = true;
                                reject(new CacherError('Transaction was aborted'));
                            }
                        };
                        tx.onerror = (e) => {
                            const error = e.target?.error;
                            console.warn('IndexedDB transaction error (suppressed):', error?.message);
                            e.stopPropagation?.();
                            safeReject(new CacherError(`Transaction error: ${error?.message || JSON.stringify(error)}`));
                        };
                        const store = tx.objectStore(this.storeName);
                        // Check if the index exists
                        if (!store.indexNames.contains(this.indexName)) {
                            safeReject(new CacherError(`Index "${this.indexName}" does not exist on store "${this.storeName}"`));
                            return;
                        }
                        const index = store.index(this.indexName);
                        const req = index.openKeyCursor(IDBKeyRange.upperBound(Date.now()));
                        let count = 0;
                        req.onerror = (e) => {
                            e.stopPropagation?.();
                            e.preventDefault?.();
                            const error = e.target?.error;
                            safeReject(new CacherError(`Failed to query expired cache entries: ${error?.message || JSON.stringify(error)}`));
                        };
                        req.onsuccess = () => {
                            if (settled)
                                return;
                            const cursor = req.result;
                            if (cursor && count < this.batchSize) {
                                count++;
                                const deleteReq = store.delete(cursor.primaryKey);
                                deleteReq.onerror = (e) => {
                                    e.stopPropagation?.();
                                    e.preventDefault?.();
                                    const error = e.target?.error;
                                    safeReject(new CacherError(`Failed to delete key ${cursor.primaryKey}: ${error?.message || JSON.stringify(error)}`));
                                };
                                deleteReq.onsuccess = () => {
                                    if (!settled)
                                        cursor.continue();
                                };
                            }
                            else if (cursor && count >= this.batchSize) {
                                // Hit batch limit, there are more items
                                safeResolve(true);
                            }
                            else {
                                // No more items
                                safeResolve(false);
                            }
                        };
                    }
                    catch (err) {
                        safeReject(err instanceof Error ? err : new CacherError(String(err)));
                    }
                };
            });
        }
    }
    async executeTransaction(mode, operation) {
        return new Promise((resolve, reject) => {
            let settled = false;
            let tx = null;
            let db = null;
            const cleanup = () => {
                clearTimeout(timeoutId);
                if (db) {
                    this.untrackConnection(db);
                    try {
                        db.close();
                    }
                    catch (e) {
                        // Ignore close errors
                    }
                }
            };
            const safeReject = (error) => {
                if (settled)
                    return;
                settled = true;
                cleanup();
                reject(error);
            };
            const safeResolve = (value) => {
                if (settled)
                    return;
                settled = true;
                resolve(value);
            };
            const timeoutId = setTimeout(() => {
                try {
                    tx?.abort();
                }
                catch (_) { /* ignore */ }
                safeReject(new CacherError('Transaction timed out'));
            }, this.transactionTimeout);
            const handle = window.indexedDB.open(this.dbName, this.dbVersion);
            handle.onerror = (e) => {
                const error = e.target?.error;
                safeReject(new CacherError(`Failed to open cache database: ${error?.message || JSON.stringify(error) || JSON.stringify(e)}`));
            };
            handle.onupgradeneeded = (e) => {
                const request = e.target;
                const database = request.result;
                if (!database) {
                    request.transaction?.abort();
                    safeReject(new CacherError("Failed to upgrade cache database"));
                    return;
                }
                try {
                    let store;
                    if (!database.objectStoreNames.contains(this.storeName)) {
                        store = database.createObjectStore(this.storeName);
                    }
                    else {
                        store = request.transaction.objectStore(this.storeName);
                    }
                    // Always ensure index exists
                    if (!store.indexNames.contains(this.indexName)) {
                        store.createIndex(this.indexName, this.indexName);
                    }
                }
                catch (err) {
                    request.transaction?.abort();
                    safeReject(err instanceof Error ? err : new CacherError(String(err)));
                }
            };
            handle.onsuccess = (event) => {
                try {
                    db = event.target.result;
                    this.trackConnection(db);
                    tx = db.transaction(this.storeName, mode);
                    tx.oncomplete = () => {
                        cleanup();
                    };
                    tx.onabort = (e) => {
                        e.stopPropagation?.();
                        cleanup();
                        if (!settled) {
                            settled = true;
                            reject(new CacherError('Transaction was aborted'));
                        }
                    };
                    tx.onerror = (e) => {
                        const error = e.target?.error;
                        console.warn('IndexedDB transaction error (suppressed):', error?.message);
                        e.stopPropagation?.();
                        safeReject(new CacherError(`Transaction error: ${error?.message || JSON.stringify(error)}`));
                    };
                    const store = tx.objectStore(this.storeName);
                    const result = operation(store, db);
                    if (result) {
                        result.onerror = (e) => {
                            e.stopPropagation?.();
                            e.preventDefault?.();
                            const error = e.target?.error;
                            safeReject(new CacherError(`Cache operation failed: ${error?.name ? error.name + ': ' : ''}${error?.message || JSON.stringify(error) || JSON.stringify(e)}`));
                        };
                        result.onsuccess = () => {
                            safeResolve(result.result);
                        };
                    }
                    else {
                        // Operation doesn't return a request (e.g., cursor operations)
                        safeResolve(undefined);
                    }
                }
                catch (err) {
                    safeReject(err instanceof Error ? err : new CacherError(String(err)));
                }
            };
        });
    }
    async deleteDBase() {
        this.closeAllConnections();
        return new Promise((res, rej) => {
            let settled = false;
            let blockedRetryInterval = null;
            const cleanupTimers = () => {
                clearTimeout(timeoutId);
                if (blockedRetryInterval) {
                    clearInterval(blockedRetryInterval);
                    blockedRetryInterval = null;
                }
            };
            const timeoutId = setTimeout(() => {
                if (settled)
                    return;
                settled = true;
                cleanupTimers();
                rej(new CacherError('Failed to delete cache database: operation timed out (blocked by open connections)'));
            }, this.purgingTimeout);
            const req = window.indexedDB.deleteDatabase(this.dbName);
            req.onerror = (e) => {
                if (settled)
                    return;
                settled = true;
                cleanupTimers();
                const error = e.target?.error;
                rej(new CacherError(`Failed to delete cache database: ${error?.message || JSON.stringify(error) || JSON.stringify(e)}`));
            };
            req.onblocked = () => {
                if (settled)
                    return;
                console.warn('IndexedDB deleteDatabase is blocked by open connections');
                this.closeAllConnections();
                // Keep retrying to close new connections that may open while blocked
                if (!blockedRetryInterval) {
                    blockedRetryInterval = setInterval(() => {
                        if (settled) {
                            cleanupTimers();
                            return;
                        }
                        this.closeAllConnections();
                    }, 500);
                }
            };
            req.onsuccess = () => {
                if (settled)
                    return;
                settled = true;
                cleanupTimers();
                res();
            };
        });
    }
    async getEstimate() {
        const estimate = await window.navigator.storage.estimate();
        if (typeof estimate.quota !== 'number' || typeof estimate.usage !== 'number' || isNaN(estimate.quota) || isNaN(estimate.usage)) {
            throw new Error(`Storage quota or usage is not a number. Quota is ${estimate.quota}. Usage is ${estimate.usage}`);
        }
        return estimate;
    }
    async purgeIfQuotaLimit() {
        if (this.isPurging)
            return false;
        this.setPurging(true);
        try {
            return await this.withRetry(async () => {
                const estimate = await this.getEstimate();
                if (estimate.quota && estimate.usage) {
                    const remainingMb = (estimate.quota - estimate.usage) / (1024 * 1024);
                    const usageMb = estimate.usage / (1024 * 1024);
                    const quotaMb = estimate.quota / (1024 * 1024);
                    // Purge if remaining space is below the threshold
                    if (remainingMb <= this.getQuotaLimitInMb()) {
                        console.warn(`Cache storage critically low. Remaining: ${remainingMb.toFixed(2)}MB, Usage: ${usageMb.toFixed(2)}MB, Total: ${quotaMb.toFixed(2)}MB, Threshold: ${this.getQuotaLimitInMb()}MB`);
                        await this.deleteDBase();
                        return true;
                    }
                }
                return false;
            });
        }
        finally {
            this.setPurging(false);
        }
    }
    getQuotaLimitInMb() {
        return this.quotaLimit;
    }
}

export { IndexedDbCacher, LocalStorageCacher };
